// Wizard steps: Texty pro Google Play + Grafika pro obchody.
import { useState } from "react";
import type { AppGoogleCopyPayload, AppScreenshotsPayload, AppAssetSlot } from "../../../shared/app-wizard";
import { APP_TEXT_LIMITS, appWizardLocales } from "../../../shared/app-wizard";
import {
  AssetUploadZone,
  FieldBlock,
  LocaleTextRows,
  SaveBadge,
  useStepSaver,
  type StepProps
} from "./wizard-shared";

const EMPTY_GOOGLE_COPY: AppGoogleCopyPayload = { shortDescription: {}, description: {} };

export function StepGoogleCopy(props: StepProps) {
  const [draft, setDraft] = useState<AppGoogleCopyPayload>(props.data.wizard.steps.google_copy ?? EMPTY_GOOGLE_COPY);
  const saver = useStepSaver("google_copy", props.save, props.readOnly);
  const locales = appWizardLocales(props.data.wizard);
  const stores = props.data.wizard.steps.basics?.stores ?? { apple: true, google: true };

  const update = (next: AppGoogleCopyPayload) => {
    setDraft(next);
    saver.queue(next as unknown as Record<string, unknown>);
  };

  if (locales.length === 0) {
    return (
      <div className="appwiz-step">
        <header className="appwiz-step-heading"><h2>{props.t("appWizard.googleCopy.title")}</h2></header>
        <p className="appwiz-need-basics">{props.t("appWizard.copy.needLanguages")}</p>
        <button className="secondary-button compact-button" type="button" onClick={() => props.onOpenStep("basics")}>
          {props.t("appWizard.copy.goBasics")}
        </button>
      </div>
    );
  }

  return (
    <div className="appwiz-step">
      <header className="appwiz-step-heading">
        <h2>{props.t("appWizard.googleCopy.title")}</h2>
        <p>{props.t("appWizard.googleCopy.copy")}</p>
        <SaveBadge state={saver.state} t={props.t} />
      </header>

      <FieldBlock label={props.t("appWizard.googleCopy.shortLabel")} hint={props.t("appWizard.googleCopy.shortHint")}>
        <LocaleTextRows
          locales={locales} value={draft.shortDescription} max={APP_TEXT_LIMITS.shortDescription}
          voice csrfToken={props.csrfToken} readOnly={props.readOnly}
          ariaLabel={props.t("appWizard.googleCopy.shortLabel")} t={props.t}
          onChange={(shortDescription) => update({ ...draft, shortDescription })}
        />
      </FieldBlock>

      {stores.apple ? (
        // With the App Store selected, the long listing description is owned
        // by the App Store texts step (reference rule) - say it, do not ask twice.
        <p className="appwiz-inline-rule">{props.t("appWizard.googleCopy.descriptionShared")}</p>
      ) : (
        <FieldBlock label={props.t("appWizard.googleCopy.descriptionLabel")} hint={props.t("appWizard.googleCopy.descriptionHint")}>
          <LocaleTextRows
            locales={locales} value={draft.description} max={APP_TEXT_LIMITS.description}
            voice csrfToken={props.csrfToken} readOnly={props.readOnly}
            ariaLabel={props.t("appWizard.googleCopy.descriptionLabel")} t={props.t}
            onChange={(description) => update({ ...draft, description })}
          />
        </FieldBlock>
      )}
    </div>
  );
}

const EMPTY_SCREENSHOTS: AppScreenshotsPayload = { assets: {} };

const SLOT_GROUPS: Array<{ key: string; store: "apple" | "google"; slots: AppAssetSlot[] }> = [
  { key: "appleIphone", store: "apple", slots: ["apple-iphone-1", "apple-iphone-2", "apple-iphone-3", "apple-iphone-4"] },
  { key: "appleIpad", store: "apple", slots: ["apple-ipad-1", "apple-ipad-2", "apple-ipad-3", "apple-ipad-4"] },
  { key: "googleFeature", store: "google", slots: ["feature-graphic"] },
  { key: "googlePhone", store: "google", slots: ["google-phone-1", "google-phone-2", "google-phone-3", "google-phone-4"] }
];

export function StepScreenshots(props: StepProps) {
  const [draft, setDraft] = useState<AppScreenshotsPayload>(props.data.wizard.steps.screenshots ?? EMPTY_SCREENSHOTS);
  const saver = useStepSaver("screenshots", props.save, props.readOnly);
  const stores = props.data.wizard.steps.basics?.stores ?? { apple: true, google: true };

  const setSlot = (slot: AppAssetSlot, attachmentId: string | null) => {
    const next = { assets: { ...draft.assets, [slot]: attachmentId } };
    setDraft(next);
    saver.queue(next as unknown as Record<string, unknown>);
  };

  const groups = SLOT_GROUPS.filter((group) => stores[group.store]);

  return (
    <div className="appwiz-step">
      <header className="appwiz-step-heading">
        <h2>{props.t("appWizard.screenshots.title")}</h2>
        <p>{props.t("appWizard.screenshots.copy")}</p>
        <SaveBadge state={saver.state} t={props.t} />
      </header>
      <p className="appwiz-inline-rule">{props.t("appWizard.screenshots.helpNote")}</p>
      {groups.map((group) => (
        <FieldBlock key={group.key} label={props.t(`appWizard.screenshots.group.${group.key}`)} hint={props.t(`appWizard.screenshots.groupHint.${group.key}`)}>
          <div className="appwiz-asset-grid">
            {group.slots.map((slot, index) => (
              <AssetUploadZone
                key={slot}
                slot={slot}
                label={group.slots.length > 1
                  ? `${props.t("appWizard.screenshots.shotLabel")} ${index + 1}`
                  : props.t(`appWizard.screenshots.group.${group.key}`)}
                attachmentId={draft.assets[slot as Exclude<AppAssetSlot, "icon">] ?? null}
                csrfToken={props.csrfToken}
                readOnly={props.readOnly}
                t={props.t}
                onUploaded={(attachmentId) => setSlot(slot, attachmentId)}
                onCleared={() => setSlot(slot, null)}
              />
            ))}
          </div>
        </FieldBlock>
      ))}
    </div>
  );
}
